"use client"
import { Heart, Activity, FlaskConical, ImageIcon, Lightbulb } from "lucide-react";

export default function ClinicalAnalysis({ clinicalAnalysis }) {
    if (!clinicalAnalysis) return null;

    const getStatusColor = (status) => {
        const s = status?.toLowerCase() || '';
        if (s === 'abnormal') return 'bg-red-100 text-red-800';
        if (s === 'borderline') return 'bg-yellow-100 text-yellow-800';
        if (s === 'normal') return 'bg-green-100 text-green-800';
        return 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="pt-2">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4 flex items-center gap-2">
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-[#0B7BC1] flex-shrink-0" />
                Clinical Analysis
            </h3>

            {/* Vital Signs Interpretation */}
            {clinicalAnalysis.vital_signs_interpretation && (
                <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <Activity className="w-4 h-4 text-[#0B7BC1]" />
                        Vital Signs Interpretation
                    </h4>
                    <p className="text-sm text-gray-700 leading-relaxed">{clinicalAnalysis.vital_signs_interpretation}</p>
                </div>
            )}

            {/* Lab Findings */}
            {clinicalAnalysis.lab_findings && clinicalAnalysis.lab_findings.length > 0 && (
                <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <h4 className="text-xs sm:text-sm font-semibold text-purple-900 mb-2 sm:mb-3 flex items-center gap-2">
                        <FlaskConical className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-purple-600 flex-shrink-0" />
                        Lab Findings
                    </h4>
                    <div className="overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
                        <table className="w-full text-xs sm:text-sm min-w-[600px]">
                            <thead>
                                <tr className="border-b border-purple-200">
                                    <th className="text-left py-1.5 sm:py-2 px-2 sm:px-3 text-purple-900 font-semibold">Parameter</th>
                                    <th className="text-left py-1.5 sm:py-2 px-2 sm:px-3 text-purple-900 font-semibold">Value</th>
                                    <th className="text-left py-1.5 sm:py-2 px-2 sm:px-3 text-purple-900 font-semibold">Status</th>
                                    <th className="text-left py-1.5 sm:py-2 px-2 sm:px-3 text-purple-900 font-semibold">Clinical Significance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {clinicalAnalysis.lab_findings.map((finding, index) => {
                                    if (typeof finding === 'string') {
                                        return (
                                            <tr key={index} className="border-b border-purple-100 last:border-b-0">
                                                <td colSpan={4} className="py-1.5 sm:py-2 px-2 sm:px-3 text-xs sm:text-sm text-purple-800 break-words">{finding}</td>
                                            </tr>
                                        );
                                    }

                                    const statusColor = getStatusColor(finding.status);

                                    return (
                                        <tr key={index} className="border-b border-purple-100 last:border-b-0">
                                            <td className="py-1.5 sm:py-2 px-2 sm:px-3 font-medium text-gray-900 break-words">{finding.parameter || '—'}</td>
                                            <td className="py-1.5 sm:py-2 px-2 sm:px-3 text-gray-800 font-mono break-words">{finding.value || '—'}</td>
                                            <td className="py-1.5 sm:py-2 px-2 sm:px-3">
                                                {finding.status ? (
                                                    <span className={`inline-block px-1.5 sm:px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium capitalize ${statusColor}`}>
                                                        {finding.status}
                                                    </span>
                                                ) : '—'}
                                            </td>
                                            <td className="py-1.5 sm:py-2 px-2 sm:px-3 text-gray-700 break-words">{finding.clinical_significance || '—'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Imaging Findings */}
            {clinicalAnalysis.imaging_findings && (
                <div className="mb-3 sm:mb-4 p-3 sm:p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
                    <h4 className="text-xs sm:text-sm font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                        <ImageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-indigo-600 flex-shrink-0" />
                        Imaging Findings
                    </h4>
                    <p className="text-xs sm:text-sm text-indigo-800">{clinicalAnalysis.imaging_findings}</p>
                </div>
            )}

            {/* Diagnostic Reasoning */}
            {clinicalAnalysis.diagnostic_reasoning && clinicalAnalysis.diagnostic_reasoning.length > 0 && (
                <div className="p-3 sm:p-4 bg-white border border-gray-200 rounded-lg">
                    <h4 className="text-xs sm:text-sm font-semibold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2">
                        <Lightbulb className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#0B7BC1] flex-shrink-0" />
                        Diagnostic Reasoning
                    </h4>
                    <ol className="space-y-2">
                        {clinicalAnalysis.diagnostic_reasoning.map((step, index) => (
                            <li key={index} className="flex gap-3">
                                <span className="flex-shrink-0 w-6 h-6 bg-[#0B7BC1] text-white rounded-full flex items-center justify-center text-xs font-medium">
                                    {index + 1}
                                </span>
                                <p className="text-sm text-gray-700 leading-relaxed pt-0.5">{step}</p>
                            </li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
}
